import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import { Turnstile } from '@marsidev/react-turnstile';

const EmployeesPage = () => {
  const navigate = useNavigate();
  const [vets, setVets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewVetModal, setShowNewVetModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [clinic, setClinic] = useState(null);
  const [vetLimitReached, setVetLimitReached] = useState(false);
  const [vetLimitInfo, setVetLimitInfo] = useState(null);
  const [newVetData, setNewVetData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    mobile_phone: ''
  });
  const [newVetErrors, setNewVetErrors] = useState({
    email: '',
    first_name: '',
    last_name: ''
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVets, setTotalVets] = useState(0);
  const vetsPerPage = 10;

  useEffect(() => {
    fetchClinicAndVets();
  }, []);

  useEffect(() => {
    if (clinic) {
      fetchVets();
    }
  }, [clinic, currentPage]);

  const fetchClinicAndVets = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: clinicData, error: clinicError } = await supabase
        .from('clinics')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (clinicError) {
        const { data: vetData, error: vetError } = await supabase
          .from('vets')
          .select('clinic_id')
          .eq('user_id', user.id)
          .single();

        if (vetError) throw clinicError;
        setClinic(vetData.clinic_id);
      } else {
        setClinic(clinicData.id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVets = async () => {
    try {
      if (!clinic) return;

      // Get total count
      const { count, error: countError } = await supabase
        .from('vets')
        .select('*', { count: 'exact', head: true })
        .eq('clinic_id', clinic);

      if (countError) throw countError;
      setTotalVets(count || 0);

      // Calculate pagination range
      const from = (currentPage - 1) * vetsPerPage;
      const to = from + vetsPerPage - 1;

      const { data: vetsData, error: vetsError } = await supabase
        .from('vets')
        .select('*')
        .eq('clinic_id', clinic)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (vetsError) throw vetsError;
      setVets(vetsData || []);

      // Check vet/user limit
      const { data: limitData, error: limitError } = await supabase.rpc('check_clinic_limit', {
        p_clinic_id: clinic,
        p_limit_type: 'users'
      });

      if (!limitError && limitData) {
        setVetLimitReached(!limitData.allowed);
        setVetLimitInfo({ limit: limitData.limit, current: limitData.current });
      } else {
        setVetLimitReached(false);
        setVetLimitInfo(null);
      }
    } catch (error) {
      console.error('Error fetching vets:', error);
    }
  };

  const handleAddVet = async (e) => {
    e.preventDefault();
    const isValid = validateNewVetForm();
    if (!isValid) return;

    try {
      const { data, error } = await supabase.functions.invoke('create-vet', {
        body: {
          email: newVetData.email,
          first_name: newVetData.first_name,
          last_name: newVetData.last_name,
          phone: newVetData.mobile_phone || null,
          clinic_id: clinic,
          turnstile_token: turnstileToken,
        }
      });

      if (error || !data?.ok) {
        console.error('Error from create-vet function:', error || data);
        setSuccessMessage(`Σφάλμα κατά τη δημιουργία κτηνίατρου: ${(error?.message || data?.error) ?? 'Άγνωστο σφάλμα'}`);
        setShowSuccessModal(true);
        return;
      }

      setNewVetData({
        email: '',
        first_name: '',
        last_name: '',
        mobile_phone: ''
      });
      setTurnstileToken('');
      turnstileRef.current?.reset();
      setShowNewVetModal(false);
      setCurrentPage(1); // Go to first page to see new vet
      await fetchVets();

      setSuccessMessage(`Ο κτηνίατρος ${newVetData.first_name} ${newVetData.last_name} προστέθηκε επιτυχώς. Εστάλη email πρόσκλησης στο ${newVetData.email} για να ορίσει τον κωδικό του.`);
      setShowSuccessModal(true);

    } catch (error) {
      console.error('Error adding vet:', error);
      setSuccessMessage(`Παρουσιάστηκε σφάλμα κατά την προσθήκη κτηνιάτρου: ${error.message}`);
      setShowSuccessModal(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewVetData(prev => ({
      ...prev,
      [name]: value
    }));
    validateNewVetField(name, value);
  };

  const validateEmail = (value) => {
    if (!value) return 'Το email είναι υποχρεωτικό.';
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(value) ? '' : 'Παρακαλώ εισάγετε ένα έγκυρο email (π.χ. name@example.com).';
  };

  const validateNewVetField = (name, value) => {
    let message = '';
    if (name === 'first_name') {
      message = value.trim() ? '' : 'Το όνομα είναι υποχρεωτικό.';
    } else if (name === 'last_name') {
      message = value.trim() ? '' : 'Το επώνυμο είναι υποχρεωτικό.';
    } else if (name === 'email') {
      message = validateEmail(value);
    }
    if (message !== undefined) {
      setNewVetErrors(prev => ({ ...prev, [name]: message }));
    }
    return message;
  };

  const validateNewVetForm = () => {
    const errors = {
      first_name: validateNewVetField('first_name', newVetData.first_name),
      last_name: validateNewVetField('last_name', newVetData.last_name),
      email: validateNewVetField('email', newVetData.email)
    };
    setNewVetErrors(errors);
    return !errors.first_name && !errors.last_name && !errors.email;
  };

  const confirmDeactivateVet = (vetId) => {
    setConfirmAction(() => async () => {
      try {
        const { data, error } = await supabase.functions.invoke('toggle-vet-status', {
          body: { vet_id: vetId, action: 'deactivate' }
        });

        if (error) throw error;
        if (!data?.ok) {
          throw new Error(data?.error || 'Η απενεργοποίηση απέτυχε.');
        }

        setVets(prev => prev.map(v => v.id === vetId ? { ...v, active: false, deleted: true } : v));
        setSuccessMessage('Ο κτηνίατρος απενεργοποιήθηκε επιτυχώς και δεν μπορεί πλέον να συνδεθεί!');
        setShowSuccessModal(true);
        await fetchVets();
      } catch (error) {
        console.error('Error deactivating vet:', error);
        setSuccessMessage(`Σφάλμα κατά την απενεργοποίηση κτηνίατρου: ${error.message || ''}`);
        setShowSuccessModal(true);
      }

      setShowConfirmModal(false);
      setConfirmAction(null);
    });
    setShowConfirmModal(true);
  };

  const confirmActivateVet = (vetId) => {
    setConfirmAction(() => async () => {
      try {
        const { data, error } = await supabase.functions.invoke('toggle-vet-status', {
          body: { vet_id: vetId, action: 'activate' }
        });

        if (error) throw error;
        if (!data?.ok) {
          throw new Error(data?.error || 'Η ενεργοποίηση απέτυχε.');
        }

        setVets(prev => prev.map(v => v.id === vetId ? { ...v, active: true, deleted: false } : v));
        setSuccessMessage('Ο κτηνίατρος ενεργοποιήθηκε επιτυχώς και μπορεί να συνδεθεί ξανά!');
        setShowSuccessModal(true);
        await fetchVets();
      } catch (error) {
        console.error('Error activating vet:', error);
        setSuccessMessage(`Σφάλμα κατά την ενεργοποίηση κτηνίατρου: ${error.message || ''}`);
        setShowSuccessModal(true);
      }

      setShowConfirmModal(false);
      setConfirmAction(null);
    });
    setShowConfirmModal(true);
  };

  // Pagination helpers
  const totalPages = Math.ceil(totalVets / vetsPerPage);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="rounded-xl overflow-hidden border p-4 lg:p-6" style={{ backgroundColor: '#111827', borderColor: '#374151', boxShadow: '0 10px 20px rgba(0,0,0,0.25)' }}>
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Εργαζόμενοι</h1>
            <p className="text-gray-400 text-base lg:text-lg">
              Διαχείριση κτηνιάτρων της κλινικής
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="mb-6 lg:mb-8">
          <button
            onClick={() => setShowNewVetModal(true)}
            disabled={vetLimitReached}
            className="inline-flex items-center px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ background: vetLimitReached ? '#4A5568' : 'linear-gradient(90deg, #10B981, #059669)' }}
            title={vetLimitReached && vetLimitInfo ? `Έχετε φτάσει το όριο κτηνιάτρων (${vetLimitInfo.current}/${vetLimitInfo.limit})` : ''}
          >
            <span className="mr-2">+</span>
            Νέος Κτηνίατρος
          </button>
          {vetLimitReached && vetLimitInfo && (
            <p className="text-sm mt-2" style={{ color: '#FCA5A5' }}>
              Έχετε φτάσει το όριο κτηνιάτρων ({vetLimitInfo.current}/{vetLimitInfo.limit}). Αναβαθμίστε το πλάνο σας για να προσθέσετε περισσότερους.
            </p>
          )}
        </div>

        {/* Vets Table */}
        <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: '#111827', borderColor: '#374151', boxShadow: '0 10px 20px rgba(0,0,0,0.25)' }}>
          <div className="px-4 lg:px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.65), rgba(31,41,55,0.65))' }}>
            <h3 className="text-lg leading-6 font-medium text-white mb-4">
              Κτηνίατροι ({totalVets})
            </h3>

            {vets.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <div className="text-gray-400 text-6xl mb-4">👨‍⚕️</div>
                <h3 className="text-lg font-medium text-white mb-2">
                  Δεν υπάρχουν κτηνίατροι
                </h3>
                <p className="text-gray-400">
                  Ξεκινήστε προσθέτοντας τον πρώτο κτηνίατρο της κλινικής.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y" style={{ borderColor: '#374151' }}>
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Όνομα
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Τηλέφωνο
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Ημ/νία Προσθήκης
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Κατάσταση</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Ενέργειες</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: '#374151' }}>
                    {vets.map((vet) => (
                      <tr key={vet.id} className="hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full flex items-center justify-center bg-blue-600/20 border border-blue-500/30">
                                <span className="text-sm font-medium text-blue-400">
                                  {vet.first_name?.[0]}{vet.last_name?.[0]}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white">
                                {vet.first_name} {vet.last_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-300">{vet.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-300">{vet.mobile_phone || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {new Date(vet.created_at).toLocaleDateString('el-GR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                            vet.active && !vet.deleted 
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                              : 'bg-red-500/10 text-red-400 border-red-500/20'
                          }`}>
                            {vet.active && !vet.deleted ? 'Ενεργός' : 'Ανενεργός'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {vet.active && !vet.deleted && (
                              <button
                                onClick={() => confirmDeactivateVet(vet.id)}
                                className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                title="Απενεργοποίηση"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                            {(!vet.active || vet.deleted) && (
                              <button
                                onClick={() => confirmActivateVet(vet.id)}
                                className="p-2 rounded-lg text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                                title="Ενεργοποίηση"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalVets > vetsPerPage && (
            <div className="px-4 lg:px-6 py-4 flex flex-col items-center space-y-4">
              <div className="text-sm text-gray-400">
                Εμφάνιση {((currentPage - 1) * vetsPerPage) + 1} - {Math.min(currentPage * vetsPerPage, totalVets)} από {totalVets} εργαζόμενους
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-white hover:bg-gray-700'
                  }`}
                  style={{ backgroundColor: currentPage === 1 ? '#4A5568' : '#2D3748' }}
                >
                  ← Προηγούμενη
                </button>

                <div className="flex items-center space-x-1">
                  {getPageNumbers().map((page, index) => (
                    <button
                      key={index}
                      onClick={() => typeof page === 'number' ? goToPage(page) : null}
                      disabled={typeof page !== 'number'}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        typeof page === 'number'
                          ? page === currentPage
                            ? 'text-white'
                            : 'text-gray-300 hover:text-white hover:bg-gray-700'
                          : 'text-gray-500 cursor-default'
                      }`}
                      style={{
                        backgroundColor: typeof page === 'number' && page === currentPage ? '#10B981' : '#2D3748'
                      }}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage >= totalPages
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-white hover:bg-gray-700'
                  }`}
                  style={{ backgroundColor: currentPage >= totalPages ? '#4A5568' : '#2D3748' }}
                >
                  Επόμενη →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Vet Modal */}
      {showNewVetModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-xl">
            <div className="rounded-xl border shadow-2xl" style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
              <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: '#374151' }}>
                <h3 className="text-xl font-semibold text-white">Νέος Κτηνίατρος</h3>
                <button
                  onClick={() => { setShowNewVetModal(false); setTurnstileToken(''); turnstileRef.current?.reset(); }}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="Κλείσιμο"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddVet} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-300 mb-2">Όνομα *</label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={newVetData.first_name}
                      onChange={handleInputChange}
                      maxLength={50}
                      aria-invalid={!!newVetErrors.first_name}
                      className={`w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 ${newVetErrors.first_name ? 'border-red-500 focus:ring-red-400/30' : 'border-gray-600 focus:ring-emerald-400/30 bg-[#111827]'}`}
                      placeholder="π.χ. Γιάννης"
                    />
                    {newVetErrors.first_name && (
                      <p className="mt-1 text-xs" style={{ color: '#FCA5A5' }}>{newVetErrors.first_name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-300 mb-2">Επώνυμο *</label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={newVetData.last_name}
                      onChange={handleInputChange}
                      maxLength={50}
                      aria-invalid={!!newVetErrors.last_name}
                      className={`w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 ${newVetErrors.last_name ? 'border-red-500 focus:ring-red-400/30' : 'border-gray-600 focus:ring-emerald-400/30 bg-[#111827]'}`}
                      placeholder="π.χ. Παπαδόπουλος"
                    />
                    {newVetErrors.last_name && (
                      <p className="mt-1 text-xs" style={{ color: '#FCA5A5' }}>{newVetErrors.last_name}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={newVetData.email}
                    onChange={handleInputChange}
                    aria-invalid={!!newVetErrors.email}
                    className={`w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 ${newVetErrors.email ? 'border-red-500 focus:ring-red-400/30' : 'border-gray-600 focus:ring-emerald-400/30 bg-[#111827]'}`}
                    placeholder="name@example.com"
                  />
                  {newVetErrors.email && (
                    <p className="mt-1 text-xs" style={{ color: '#FCA5A5' }}>{newVetErrors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="mobile_phone" className="block text-sm font-medium text-gray-300 mb-2">Τηλέφωνο (προαιρετικό)</label>
                  <input
                    type="tel"
                    id="mobile_phone"
                    name="mobile_phone"
                    value={newVetData.mobile_phone}
                    onChange={handleInputChange}
                    maxLength={20}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-600 bg-[#111827] text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                    placeholder="π.χ. 69XXXXXXXX"
                  />
                </div>

                <div className="pt-2">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onError={() => setTurnstileToken('')}
                    onExpire={() => setTurnstileToken('')}
                    theme="dark"
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-700">
                  <button
                    type="submit"
                    disabled={!!(newVetErrors.email || newVetErrors.first_name || newVetErrors.last_name) || !newVetData.email || !newVetData.first_name || !newVetData.last_name || !turnstileToken}
                    className="flex-1 py-3 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
                  >
                    Προσθήκη
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowNewVetModal(false); setTurnstileToken(''); turnstileRef.current?.reset(); }}
                    className="flex-1 py-3 rounded-lg font-medium text-gray-300 transition-colors bg-[#374151] hover:bg-gray-600 border border-gray-600"
                  >
                    Ακύρωση
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-xl border max-w-sm w-full shadow-2xl bg-[#1F2937] border-gray-700">
            <div className="px-6 py-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-5 bg-blue-500/10 border border-blue-500/20">
                <svg className="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Ενημέρωση</h3>
              <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
                {successMessage}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-700 bg-[#111827] rounded-b-xl">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full py-2.5 text-sm font-medium text-white rounded-lg transition-colors bg-blue-600 hover:bg-blue-700"
              >
                Εντάξει
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-xl border max-w-sm w-full shadow-2xl bg-[#1F2937] border-gray-700">
            <div className="px-6 py-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-5 bg-amber-500/10 border border-amber-500/20">
                <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Επιβεβαίωση</h3>
              <p className="text-gray-400 text-sm">
                Είστε σίγουροι ότι θέλετε να προχωρήσετε με αυτή την ενέργεια;
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-700 flex gap-3 bg-[#111827] rounded-b-xl">
              <button
                onClick={() => confirmAction && confirmAction()}
                className="flex-1 py-2.5 rounded-lg font-medium text-white transition-colors bg-red-600 hover:bg-red-700 shadow-md"
              >
                Ναι, σίγουρα
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
                className="flex-1 py-2.5 rounded-lg font-medium text-gray-300 transition-colors bg-[#374151] hover:bg-gray-600 border border-gray-600"
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;